import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, Button, message, Tooltip, Modal } from 'antd';
import { UploadOutlined, EyeOutlined, CloseCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { getSignedUrl } from 'store/commonSlice';

const FileActionsContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
`;

const FileName = styled.span`
	max-width: 180px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 14px;
	color: #1890ff;
`;

const IconBtn = styled(Button)`
	border: none !important;
	background: none !important;
	box-shadow: none !important;
	padding: 0 4px !important;
	min-width: 0 !important;
	height: 28px !important;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export default function UploadWithPreview({
	beforeUpload,
	uploading,
	disabled,
	uploadError,
	uploadedFile,
	onFileRemove,
	buttonText = 'Upload Proof',
	accept = ['.pdf', '.jpg', '.jpeg', '.png'],
	entity = 'trip',
	hideRemove = false,
}) {
	const dispatch = useDispatch();
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');

	// Check if a file is uploaded to disable the upload button
	const hasUploadedFile = uploadedFile && uploadedFile.length > 0;

	// Helper function to get button text
	const getButtonText = () => {
		if (uploading) return 'Uploading...';
		if (hasUploadedFile) return 'File Uploaded';
		return buttonText;
	};

	const handleFilePreview = async (filename) => {
		if (filename) {
			try {
				const data = {
					url_type: 'view',
					data: {
						entity,
						keys: [filename],
					},
				};
				const res = await dispatch(getSignedUrl(data));
				if (res?.data && res.data[0]?.url) {
					setPreviewUrl(res.data[0].url);
					setPreviewTitle(filename);
					setPreviewVisible(true);
				} else {
					message.info('Preview not available');
				}
			} catch (err) {
				message.info('Preview not available');
			}
		}
	};

	const handleFileRemove = () => {
		if (onFileRemove) {
			const result = onFileRemove();
			// Only show success message if removal was successful (not explicitly false)
			if (result !== false) {
				message.success('File removed');
			}
		}
	};

	const getFileExtension = (filename) => filename.split('.').pop().toLowerCase();

	const renderPreviewContent = () => {
		const fileExt = getFileExtension(previewTitle);

		if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt)) {
			return (
				<img
					alt={previewTitle}
					style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
					src={previewUrl}
				/>
			);
		}
		if (fileExt === 'pdf') {
			return (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					<div style={{ marginBottom: '20px' }}>
						<p style={{ fontSize: '16px', marginBottom: '10px' }}>
							PDF Preview: {previewTitle}
						</p>
						<p style={{ color: '#666', marginBottom: '20px' }}>
							For better PDF viewing experience, please open in a new tab
						</p>
					</div>
					<div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
						<Button
							type="primary"
							onClick={() => window.open(previewUrl, '_blank')}
							size="large"
						>
							Open PDF in New Tab
						</Button>
					</div>
					{/* Fallback iframe for browsers that support it */}
					<div
						style={{
							marginTop: '20px',
							border: '1px solid #d9d9d9',
							borderRadius: '4px',
						}}
					>
						<iframe
							title={previewTitle}
							src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
							width="100%"
							height="400px"
							style={{ border: 'none' }}
							onError={() => {
								// If iframe fails, it will be hidden and user can use buttons above
							}}
						/>
					</div>
				</div>
			);
		}
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				<p>Preview not available for this file type.</p>
				<Button type="primary" onClick={() => window.open(previewUrl, '_blank')}>
					Open in New Tab
				</Button>
			</div>
		);
	};

	return (
		<div>
			<Upload
				showUploadList={false}
				accept={accept.join(',')}
				beforeUpload={beforeUpload}
				disabled={disabled || hasUploadedFile}
			>
				<Button
					icon={<UploadOutlined />}
					loading={uploading}
					disabled={uploading || disabled || hasUploadedFile}
				>
					{getButtonText()}
				</Button>
			</Upload>
			{uploadError && <div style={{ color: 'red', marginTop: 4 }}>{uploadError}</div>}
			{uploadedFile && (
				<FileActionsContainer>
					<FileName title={uploadedFile}>Uploaded: {uploadedFile}</FileName>
					<Tooltip title="Preview">
						<IconBtn
							type="text"
							icon={<EyeOutlined style={{ color: '#1890ff' }} />}
							onClick={() => handleFilePreview(uploadedFile)}
							disabled={disabled}
						/>
					</Tooltip>
					{!hideRemove && (
						<Tooltip title="Remove">
							<IconBtn
								type="text"
								icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
								onClick={handleFileRemove}
								disabled={disabled}
							/>
						</Tooltip>
					)}
				</FileActionsContainer>
			)}

			<Modal
				open={previewVisible}
				title={previewTitle}
				footer={null}
				onCancel={() => setPreviewVisible(false)}
				centered
				width={getFileExtension(previewTitle) === 'pdf' ? 1000 : 800}
				bodyStyle={{ textAlign: 'center', background: '#fff' }}
			>
				{renderPreviewContent()}
			</Modal>
		</div>
	);
}

UploadWithPreview.propTypes = {
	beforeUpload: PropTypes.func.isRequired,
	uploading: PropTypes.bool,
	disabled: PropTypes.bool,
	uploadError: PropTypes.string,
	uploadedFile: PropTypes.string,
	onFileRemove: PropTypes.func,
	buttonText: PropTypes.string,
	accept: PropTypes.array,
	entity: PropTypes.string,
	hideRemove: PropTypes.bool,
};
